// When to ask for a barcode, and what to do with the one the user scans. Every wine that has no
// barcode of its own on file is offered one: a catalogue wine with no linked GTIN, and a wine only
// this device knows. We ask once per wine per session and only if About says so, and the answer is
// queued like any other contribution (./outbox.ts), so scanning never waits on the network either.
// A wine this device already submitted a barcode for is not asked about again, while the submission
// waits and after the edge accepted it, until the catalogue itself holds the code.
import * as catalogue from "../catalogue";
import {
  forgetSubmittedBarcode,
  getContributePrefs,
  rememberSubmittedBarcode,
  submittedBarcodeFor,
} from "./preferences";
import { enqueue } from "./outbox";
import type { WineSuggestion } from "./suggest";

/** Suggestion keys already asked about this session; "not now" counts as asked. */
const asked = new Set<string>();

/**
 * True when picking `wine` should prompt for its barcode, and marks it asked so the same wine
 * never prompts twice. The suggestion key is stable per wine, catalogue or local.
 */
export function takeBarcodePrompt(wine: WineSuggestion): boolean {
  if (!getContributePrefs().askBarcode) return false;
  if (wine.hasBarcode) {
    // The catalogue has a barcode for it now, ours or another: nothing left to remember.
    forgetSubmittedBarcode(wine.key);
    return false;
  }
  if (submittedBarcodeFor(wine.key) !== undefined) return false;
  if (asked.has(wine.key)) return false;
  asked.add(wine.key);
  return true;
}

/**
 * Drops the memory of every submitted barcode the catalogue has since learned: a wine the user
 * scanned a code for, once the catalogue lists that code, is done and need not be remembered. The
 * offline catalogue changes only on a download, so this runs when the app starts.
 */
export async function pruneSubmittedBarcodes(): Promise<void> {
  if (!catalogue.isCatalogueReady()) return;
  for (const [key, submitted] of Object.entries(getContributePrefs().submittedBarcodes)) {
    try {
      const match = await catalogue.lookupByGtin(submitted.gtin);
      if (match.wine !== undefined || match.products.length > 0) forgetSubmittedBarcode(key);
    } catch {
      // No catalogue to ask right now: the memory stays until the next start.
      return;
    }
  }
}

/** Test seam: forget what was asked this session. */
export function resetBarcodePrompted(): void {
  asked.clear();
}

/**
 * Queues `gtin` as this wine's barcode. A code the catalogue already holds for this very wine (a
 * stale local copy, or a second scan) is not queued: `known`. `producerFallback` names the producer
 * when the catalogue row carries none.
 */
export async function submitBarcode(
  wine: WineSuggestion,
  gtin: string,
  producerFallback: string,
): Promise<"queued" | "known" | "unsendable"> {
  try {
    const match = await catalogue.lookupByGtin(gtin);
    if (wine.wineId !== undefined && match.wine?.id === wine.wineId) return "known";
  } catch {
    // No catalogue to ask: the edge deduplicates anyway.
  }
  // Remembered before queueing: a flush may confirm it before this function returns.
  rememberSubmittedBarcode(wine.key, gtin);
  const queued = enqueue({
    kind: "barcode",
    gtin,
    wine: {
      producer: wine.producer ?? producerFallback,
      name: wine.name,
      ...(wine.vintages.length === 1 && wine.vintages[0] !== undefined
        ? { vintage: wine.vintages[0] }
        : {}),
    },
    ...(wine.wineId === undefined ? {} : { catalogueWineId: String(wine.wineId) }),
  });
  // `enqueue` answers false for a duplicate as well as for nothing to send; a duplicate is fine.
  if (queued || (wine.producer ?? producerFallback).trim() !== "") return "queued";
  forgetSubmittedBarcode(wine.key);
  return "unsendable";
}
